import { useEffect, useState } from 'react';
import { trackPurchaseComplete, trackEvent } from '../utils/analytics';
import { DOWNLOAD_URL, SUPPORT_EMAIL } from '../utils/constants';

/**
 * Post-checkout banner. Stripe redirects back with ?purchase=success|cancelled
 * after the one-time app purchase.
 *
 * The GA purchase event fires once per session (sessionStorage guard) so a
 * refresh of the success URL doesn't double-count revenue, and the query
 * params are stripped so the state doesn't survive bookmarks/shares.
 */
const PurchaseSuccess = () => {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const purchase = params.get('purchase');

    let next = null;
    if (purchase === 'success') next = 'purchase-success';
    else if (purchase === 'cancelled') next = 'purchase-cancelled';
    if (!next) return;

    setStatus(next);

    const guardKey = `solidly.tracked.${next}`;
    if (!sessionStorage.getItem(guardKey)) {
      sessionStorage.setItem(guardKey, '1');
      if (next === 'purchase-success') {
        trackPurchaseComplete(25);
      } else {
        trackEvent('checkout_cancelled', { event_category: 'engagement' });
      }
    }

    params.delete('purchase');
    const query = params.toString();
    window.history.replaceState(
      {},
      document.title,
      window.location.pathname + (query ? `?${query}` : '')
    );
  }, []);

  if (!status) return null;
  if (status === 'purchase-cancelled') {
    return (
      <div className="purchase-banner purchase-banner--muted" role="status">
        <span>Checkout cancelled — no charge was made. Questions? {SUPPORT_EMAIL}</span>
        <button className="purchase-banner-close" onClick={() => setStatus(null)} aria-label="Dismiss">
          ✕
        </button>
      </div>
    );
  }

  return (
    <div className="purchase-banner" role="status">
      <div className="purchase-banner-body">
        <strong>You’re in! 🎉</strong>
        <span>
          Grab the Windows installer, then paste the license key from your email on first launch —
          that’s the whole setup. (The key takes a minute to arrive; check spam.)
        </span>
      </div>
      <a
        className="purchase-banner-download"
        href={DOWNLOAD_URL}
        onClick={() =>
          trackEvent('installer_download', {
            event_category: 'engagement',
            source: 'purchase_success',
          })
        }
      >
        Download for Windows
      </a>
      <button className="purchase-banner-close" onClick={() => setStatus(null)} aria-label="Dismiss">
        ✕
      </button>
    </div>
  );
};

export default PurchaseSuccess;
